// Shindengen ECU security-access seed -> key calculator.
// Algorithm + signatures reverse-engineered from the reference flasher.
// key = f(seed, C0,C1,C2) where C0/C1/C2 are 16-bit constants stored in the firmware.

const WRITE_SIG = [0x11, 0x12, 0x13, 0x14, 0x15, 0x15, 0x17];
const RECOVERY_SIG = [0x91, 0x91, 0x0d, 0x00, 0x9e, 0x8d];
const V850_SIG = [0x53, 0x56, 0x38, 0x35, 0x30]; // "SV850"

const u16le = (b, p) => b[p] | (b[p + 1] << 8);
const asU8 = (buf) => (buf instanceof Uint8Array ? buf : new Uint8Array(buf));

function indexOfSeq(buf, seq, from = 0) {
  for (let i = from; i <= buf.length - seq.length; i++) {
    let ok = true;
    for (let j = 0; j < seq.length; j++) if (buf[i + j] !== seq[j]) { ok = false; break; }
    if (ok) return i;
  }
  return -1;
}

// 16-bit key from seed + constants (identical formula for write & recovery).
// This variant is user-confirmed for Shindengen RH850 (K1AA/K1AL).
export function computeKey(seed16, C0, C1, C2) {
  const t = (C1 * ((seed16 + C0) & 0xffff)) & 0xffff;
  const v = C2 !== 0 ? (t + (seed16 % C2)) & 0xffff : (t + seed16) & 0xffff;
  return v & 0xffff;
}

// Documented Honda/Shindengen seed->key (UnlockECU / HondaReflashTool). Used by the older
// V850 core (e.g. 30400-K25G BEAT110), which rejects the RH850 variant above.
//   key = seed * k1;  if (k2 != 0) key %= k2;  key ^= (k0 + seed);  key &= 0xFFFF
export function computeKeyHonda(seed16, k0, k1, k2) {
  let key = (seed16 * k1) >>> 0;
  if (k2 !== 0) key = key % k2;
  key = (key ^ ((k0 + seed16) >>> 0)) >>> 0;
  return key & 0xffff;
}

// WRITE key constants: find signature, advance to marker (b[p+8]==0xEE, b[p+9] in {00,FF})
export function findWriteConstants(buf) {
  const b = asU8(buf);
  let i = indexOfSeq(b, WRITE_SIG);
  while (i !== -1) {
    let p = i + WRITE_SIG.length;
    while (p <= b.length - 10) {
      if (b[p + 8] === 0xee && (b[p + 9] === 0x00 || b[p + 9] === 0xff)) {
        return { sigOffset: i, constOffset: p, C0: u16le(b, p), C1: u16le(b, p + 2), C2: u16le(b, p + 4) };
      }
      p++;
    }
    i = indexOfSeq(b, WRITE_SIG, i + 1);
  }
  return null;
}

// RECOVERY key constants: constants are the 6 bytes immediately before the signature
export function findRecoveryConstants(buf) {
  const b = asU8(buf);
  let q = indexOfSeq(b, RECOVERY_SIG);
  while (q !== -1) {
    if (q >= 6) {
      const p = q - 6;
      return { sigOffset: q, constOffset: p, C0: u16le(b, p), C1: u16le(b, p + 2), C2: u16le(b, p + 4) };
    }
    q = indexOfSeq(b, RECOVERY_SIG, q + 1);
  }
  return null;
}

export function detectMcu(buf) {
  return indexOfSeq(asU8(buf), V850_SIG) !== -1 ? "V850" : "unknown";
}

// EXPERIMENTAL "seed-key OFF" patch: neutralize the firmware seed-key constants to echo mode
// (C0=0x0000, C1=0x0001, C2=0x0001) so the computed key equals the seed (key = seed) for every
// seed. Applied to both the write and recovery blocks. Returns a NEW buffer + patch log.
const ECHO_BYTES = [0x00, 0x00, 0x01, 0x00, 0x01, 0x00]; // C0=0000 C1=0001 C2=0001 (LE)
export function patchSeedKeyOff(buf) {
  const src = asU8(buf);
  const out = new Uint8Array(src); // copy, do not mutate source
  const patches = [];
  const apply = (label, block) => {
    if (!block) return;
    const p = block.constOffset;
    const before = Array.from(out.slice(p, p + 6));
    for (let i = 0; i < 6; i++) out[p + i] = ECHO_BYTES[i];
    patches.push({ label, offset: p, before, after: [...ECHO_BYTES] });
  };
  apply("Write", findWriteConstants(src));
  apply("Recovery", findRecoveryConstants(src));
  return { out, patches };
}

export const bytesHex = (arr) => arr.map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ");

// Copy the seed-key constants FROM a source (original) bin INTO a target (other) bin.
// The 6 constant bytes (C0,C1,C2) of each block are written at the TARGET's own block offset,
// so the target keeps its structure but now yields the same key as the source.
export function copySeedKey(srcBuf, tgtBuf) {
  const src = asU8(srcBuf);
  const tgt = asU8(tgtBuf);
  const out = new Uint8Array(tgt); // copy target
  const ops = [];
  const doCopy = (label, srcBlock, tgtBlock) => {
    if (!srcBlock) { ops.push({ label, ok: false, reason: "source block not found" }); return; }
    if (!tgtBlock) { ops.push({ label, ok: false, reason: "target block not found" }); return; }
    const srcBytes = Array.from(src.slice(srcBlock.constOffset, srcBlock.constOffset + 6));
    const before = Array.from(out.slice(tgtBlock.constOffset, tgtBlock.constOffset + 6));
    for (let i = 0; i < 6; i++) out[tgtBlock.constOffset + i] = srcBytes[i];
    ops.push({ label, ok: true, srcOffset: srcBlock.constOffset, tgtOffset: tgtBlock.constOffset, before, after: srcBytes });
  };
  doCopy("Write", findWriteConstants(src), findWriteConstants(tgt));
  doCopy("Recovery", findRecoveryConstants(src), findRecoveryConstants(tgt));
  return { out, ops };
}

// parse a seed string ("12 34", "0x1234", "1234") -> { seed16, bytes }
export function parseSeed(str) {
  const clean = String(str || "").replace(/0x/gi, "").replace(/[^0-9a-fA-F]/g, "");
  if (clean.length === 0) return null;
  const hex = clean.length % 2 ? "0" + clean : clean;
  const bytes = [];
  for (let i = 0; i < hex.length; i += 2) bytes.push(parseInt(hex.slice(i, i + 2), 16));
  const b0 = bytes[0] ?? 0, b1 = bytes[1] ?? 0;
  return { seed16: ((b0 << 8) | b1) & 0xffff, bytes };
}

export const keyHex = (v) =>
  ((v >> 8) & 0xff).toString(16).padStart(2, "0").toUpperCase() +
  (v & 0xff).toString(16).padStart(2, "0").toUpperCase();

export const hex16 = (v) => "0x" + (v & 0xffff).toString(16).padStart(4, "0").toUpperCase();
export const hexOff = (v) => "0x" + v.toString(16).toUpperCase();
